import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldAlert, Eye, EyeOff, Lock, KeyRound, Loader2, Trash2, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PII_LABELS = {
  passportNumber: 'Passport Number',
  panCard: 'PAN',
  aadharCard: 'Aadhaar',
};

const AUTO_HIDE_MS = 45000; // reveal auto-hides after 45s

const tripLabel = (t) =>
  `${t.destination || 'Trip'} · ${t.displayTripId || t.tripId || 'ID pending'}`;

/**
 * BATCH 1 — Sensitive-field (PAN / Passport / Aadhaar) access control card.
 * - Admin / Accounts: masked by default, explicit audited "Reveal" (POST
 *   /reveal-pii) + per-booking grant management for agents.
 * - Agent: no standing access; can reveal only when an admin/accounts has
 *   issued a per-booking grant (select the trip → Reveal).
 */
export default function SensitivePii({ ucc, traveller, trips = [], role, showGrantManager = true }) {
  const isPriv = role === 'admin' || role === 'accounts';
  const isAgent = role === 'agent';

  const present = {
    passportNumber: !!traveller?.hasPassport,
    panCard: !!traveller?.hasPan,
    aadharCard: !!traveller?.hasAadhaar,
  };
  const anyPresent = present.passportNumber || present.panCard || present.aadharCard;

  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [revealTrip, setRevealTrip] = useState('');
  const hideTimer = useRef(null);

  // Security: hide any revealed PII the moment the app is backgrounded or the
  // phone locks (mobile), so a value never lingers on-screen behind the auto-
  // hide window. Also clears the pending timer on unmount.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        setRevealed(null);
        if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  // Grant management (admin / accounts only)
  const [agents, setAgents] = useState([]);
  const [grants, setGrants] = useState([]);
  const [grantAgent, setGrantAgent] = useState('');
  const [grantTrip, setGrantTrip] = useState('');
  const [granting, setGranting] = useState(false);

  const loadGrants = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/pii-grants`, {
        params: { ucc }, withCredentials: true,
      });
      setGrants(Array.isArray(r.data) ? r.data : []);
    } catch { setGrants([]); }
  }, [ucc]);

  useEffect(() => {
    if (!isPriv || !showGrantManager) return;
    axios.get(`${BACKEND_URL}/api/pii-grants/agents`, { withCredentials: true })
      .then((r) => setAgents(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAgents([]));
    loadGrants();
  }, [isPriv, showGrantManager, loadGrants]);

  const doReveal = async () => {
    if (isAgent && !revealTrip) {
      toast.error('Select the booking you were granted access for');
      return;
    }
    setRevealing(true);
    try {
      const body = { fields: ['passportNumber', 'panCard', 'aadharCard'] };
      if (isAgent) body.tripId = revealTrip;
      const r = await axios.post(`${BACKEND_URL}/api/clients/${ucc}/reveal-pii`, body, { withCredentials: true });
      setRevealed(r.data?.revealed || {});
      toast.success('Revealed · this access has been logged');
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setRevealed(null), AUTO_HIDE_MS);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'You are not permitted to reveal this';
      toast.error(typeof msg === 'string' ? msg : 'Reveal denied');
    } finally {
      setRevealing(false);
    }
  };

  const issueGrant = async () => {
    if (!grantAgent || !grantTrip) {
      toast.error('Pick an agent and a booking');
      return;
    }
    setGranting(true);
    try {
      const expiresAt = new Date(Date.now() + 24 * 3600 * 1000).toISOString();
      await axios.post(`${BACKEND_URL}/api/pii-grants`, {
        agentUserId: grantAgent, clientUcc: ucc, tripId: grantTrip, expiresAt,
      }, { withCredentials: true });
      toast.success('Access granted for 24 hours');
      setGrantAgent(''); setGrantTrip('');
      loadGrants();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not grant access');
    } finally {
      setGranting(false);
    }
  };

  const revokeGrant = async (grantId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/pii-grants/${grantId}`, { withCredentials: true });
      toast.success('Access revoked');
      loadGrants();
    } catch {
      toast.error('Could not revoke');
    }
  };

  const agentName = (uid) => agents.find((a) => a.user_id === uid)?.name
    || grants.find((g) => g.agentUserId === uid)?.agentEmail || uid;

  return (
    <Card className="p-5 bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="sensitive-pii-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-rose-500" />
          Sensitive Identity Fields
        </h2>
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
          Access logged
        </span>
      </div>

      {!anyPresent ? (
        <p className="text-sm text-slate-500 py-4" data-testid="pii-none">
          No PAN, passport, or Aadhaar on file for this traveller.
        </p>
      ) : (
        <>
          <div className="space-y-2" data-testid="pii-fields">
            {Object.keys(PII_LABELS).filter((f) => present[f]).map((f) => (
              <div key={f} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50">
                <span className="text-sm text-slate-600">{PII_LABELS[f]}</span>
                <span
                  className={`text-sm font-mono ${revealed && revealed[f] ? 'text-slate-900' : 'text-slate-400'}`}
                  data-testid={`pii-value-${f}`}
                >
                  {revealed && revealed[f] ? revealed[f] : '•••• •••• ••••'}
                </span>
              </div>
            ))}
          </div>

          {/* Agent — must pick a granted booking before revealing */}
          {isAgent && (
            <div className="mt-3">
              <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-2">
                <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>You have no standing access. Reveal is possible only for a booking an admin/accounts has granted you.</span>
              </div>
              <div className="flex items-center gap-2">
                <Select value={revealTrip} onValueChange={setRevealTrip}>
                  <SelectTrigger className="h-9 flex-1" data-testid="pii-reveal-trip-select">
                    <SelectValue placeholder="Select granted booking" />
                  </SelectTrigger>
                  <SelectContent>
                    {trips.map((t) => (
                      <SelectItem key={t.tripId || t.id} value={t.tripId || t.id}>{tripLabel(t)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={doReveal} disabled={revealing} className="h-9 bg-slate-900 hover:bg-slate-800 text-white" data-testid="pii-reveal-btn">
                  {revealing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eye className="h-4 w-4 mr-1.5" /> Reveal</>}
                </Button>
              </div>
            </div>
          )}

          {/* Admin / Accounts — one-click audited reveal */}
          {isPriv && (
            <div className="mt-3 flex items-center gap-2">
              {revealed ? (
                <Button onClick={() => setRevealed(null)} variant="outline" className="h-9" data-testid="pii-hide-btn">
                  <EyeOff className="h-4 w-4 mr-1.5" /> Hide
                </Button>
              ) : (
                <Button onClick={doReveal} disabled={revealing} className="h-9 bg-slate-900 hover:bg-slate-800 text-white" data-testid="pii-reveal-btn">
                  {revealing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eye className="h-4 w-4 mr-1.5" /> Reveal (logged)</>}
                </Button>
              )}
            </div>
          )}
        </>
      )}

      {/* Grant management — admin / accounts (desktop only) */}
      {isPriv && showGrantManager && (
        <div className="mt-5 pt-4 border-t border-slate-100" data-testid="pii-grant-manager">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
            <KeyRound className="h-4 w-4 text-teal-600" />
            Agent access grants
          </h3>
          <div className="flex flex-col sm:flex-row items-stretch gap-2">
            <Select value={grantAgent} onValueChange={setGrantAgent}>
              <SelectTrigger className="h-9 flex-1" data-testid="grant-agent-select">
                <SelectValue placeholder="Agent" />
              </SelectTrigger>
              <SelectContent>
                {agents.map((a) => (
                  <SelectItem key={a.user_id} value={a.user_id}>{a.name || a.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={grantTrip} onValueChange={setGrantTrip}>
              <SelectTrigger className="h-9 flex-1" data-testid="grant-trip-select">
                <SelectValue placeholder="Booking" />
              </SelectTrigger>
              <SelectContent>
                {trips.map((t) => (
                  <SelectItem key={t.tripId || t.id} value={t.tripId || t.id}>{tripLabel(t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={issueGrant} disabled={granting} className="h-9 bg-teal-600 hover:bg-teal-700 text-white" data-testid="grant-issue-btn">
              {granting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Grant 24h'}
            </Button>
          </div>

          {grants.length > 0 ? (
            <ul className="mt-3 space-y-1.5" data-testid="grant-list">
              {grants.map((g) => (
                <li key={g.grantId} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100">
                  <span className="flex items-center gap-2 min-w-0">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span className="truncate">
                      <span className="font-medium text-slate-800">{agentName(g.agentUserId)}</span>
                      <span className="text-slate-500"> · {g.tripId}</span>
                      {g.expiresAt && <span className="text-slate-400"> · expires {new Date(g.expiresAt).toLocaleString('en-IN')}</span>}
                    </span>
                  </span>
                  <button
                    onClick={() => revokeGrant(g.grantId)}
                    className="text-rose-600 hover:text-rose-700 shrink-0 ml-2"
                    data-testid={`grant-revoke-${g.grantId}`}
                    title="Revoke"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-xs text-slate-400">No active agent grants for this traveller.</p>
          )}
        </div>
      )}
    </Card>
  );
}
